// The account the webapp is acting in. It is either the account the user signed
// in to (the "home" account, i.e. the Frontegg tenant) or one of the child
// accounts GET /accounts lists in the home account's `childAccountIds`. The
// Frontegg tenant itself never changes — only the X-Tenant-Id the webapp sends.

export const ACCOUNT_MODE_SETTING_KEY = "account_mode";
export const SANDBOX_ACCOUNT_MODE = "sandbox";

const STORAGE_PREFIX = "active-account:";

export const getActiveAccountStorageKey = (
  homeTenantId: string,
  userId: string
): string => `${STORAGE_PREFIX}${homeTenantId}:${userId}`;

export const readPersistedAccountId = (storageKey: string): string | null => {
  try {
    return localStorage.getItem(storageKey) || null;
  } catch {
    return null;
  }
};

export const persistAccountId = (
  storageKey: string,
  accountId: string | null
): void => {
  try {
    if (accountId) {
      localStorage.setItem(storageKey, accountId);
    } else {
      localStorage.removeItem(storageKey);
    }
  } catch {
    // Storage unavailable — the choice lasts for this page load only.
  }
};

// `undefined` means the home account's child list has not been read yet.
export type ChildAccountIds = string[] | undefined;

/**
 * GET /accounts lists the child account ids as an array of strings. Anything
 * else — missing, not an array, an array holding a non-string — means no
 * children.
 */
export const parseChildAccountIds = (value: unknown): string[] => {
  if (
    !Array.isArray(value) ||
    !value.every((item) => typeof item === "string")
  ) {
    return [];
  }
  const ids = (value as string[]).map((id) => id.trim()).filter(Boolean);
  return Array.from(new Set(ids));
};

/**
 * The requested account is honoured only while the home account still lists
 * it as a child; anything else — no request, no children, an unlisted id — is
 * the home account.
 */
export const resolveActiveAccountId = (
  homeTenantId: string | null,
  requestedAccountId: string | null,
  childAccountIds: ChildAccountIds
): string | null => {
  if (
    requestedAccountId &&
    requestedAccountId !== homeTenantId &&
    childAccountIds?.includes(requestedAccountId)
  ) {
    return requestedAccountId;
  }
  return homeTenantId;
};
